import { mediaPlaybackSlice } from '../../../../media-playback/slice'
import { useDispatch, useSelector } from 'react-redux'
import {
  useRef,
  useState,
  useEffect,
  MouseEvent,
  ChangeEvent,
  useCallback,
  RefObject,
} from 'react'
import { VideoProps } from '../../VideoPanel/types'
import { isElementInViewport, useInViewPort } from 'hooks/useInViewPort'
import { logger } from 'helpers/logger'
import useEventListener from 'hooks/useEventListener'
import { debounce } from 'lodash'
import useSetActiveVideo, { SLIDE_TRANSITION_DELAY } from '../useSetActiveVideo'
import { useVideoContext } from '../../ActiveVideoContext'

type VideoControlProps = Pick<
  VideoProps,
  'inView' | 'canPlayInModal' | 'canPlayInline'
> & {
  id: string
  wrapperRef: RefObject<HTMLDivElement>
}

const useVideoControl = ({
  id,
  inView,
  wrapperRef,
  canPlayInline,
  canPlayInModal,
}: VideoControlProps) => {
  const dispatch = useDispatch()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const playPromiseRef = useRef<Promise<void> | null>(null)

  const { getCurrentTime } = useVideoContext()

  const [isPlaying, setIsPlaying] = useState(false)

  const videoConfig = useSelector(
    mediaPlaybackSlice.selectors.selectVideoConfig,
  )

  const { isVisible } = useInViewPort(wrapperRef, SLIDE_TRANSITION_DELAY)

  const isOnScreen = !!(isVisible && inView)
  const canPlay = (canPlayInline || canPlayInModal) && isOnScreen
  const canStartPlay = canPlay && videoConfig.autoplay

  const volume = videoConfig.volume
  const muted = volume === 0

  const [activeVideo, setActiveVideo] = useSetActiveVideo({
    id,
    videoRef,
    isOnScreen,
    canPlayInModal,
  })

  const play = (video: HTMLVideoElement) => {
    const allVideos = Array.from(document.querySelectorAll('video'))
    const playingOnScreen = allVideos.filter(
      (v) => isElementInViewport(v) && !v.paused,
    )
    playingOnScreen.forEach((v) => v.pause())

    video.volume = videoConfig.volume || 0
    video.currentTime = getCurrentTime()

    playPromiseRef.current = video
      .play()
      .then(() => {
        playPromiseRef.current = null
        if (id !== activeVideo?.id) {
          setActiveVideo({
            id,
            ref: videoRef,
          })
        }
      })
      .catch((error: Error) => {
        playPromiseRef.current = null

        if (error.name !== 'AbortError') {
          logger.error('Playback failed:', error)
        }
      })
  }

  const togglePlay = () => {
    const video = videoRef.current

    if (video?.paused) {
      play(video)
    } else {
      pauseVideo()
    }
  }

  const onPause = () => setIsPlaying(false)

  const pauseVideo = () => {
    if (playPromiseRef.current) {
      void playPromiseRef.current
        .then(() => {
          videoRef.current?.pause()
          onPause()
        })
        .catch(() => {
          videoRef.current?.pause()
          onPause()
        })
    } else {
      videoRef.current?.pause()
      onPause()
    }
  }

  const toggleMute = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const video = videoRef.current

    if (!video) return

    const isMuted = video?.muted

    const newMutedState = isMuted ? false : true
    const newVolume = newMutedState ? 0 : 1
    video.muted = newMutedState
    video.volume = newVolume

    dispatch(
      mediaPlaybackSlice.actions.setVideoConfig({
        volume: newVolume,
      }),
    )
  }

  const debouncedDispatchRef = useRef(
    debounce((volume: number) => {
      dispatch(
        mediaPlaybackSlice.actions.setVideoConfig({
          volume,
        }),
      )
    }, 300),
  )

  useEffect(() => {
    return () => {
      debouncedDispatchRef.current.cancel()
    }
  }, [])

  const handleVolumeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current
      if (!video) return

      const volume = Number(e.target.value) / 100
      video.volume = volume

      if (video.muted && volume > 0) {
        video.muted = false
      }

      debouncedDispatchRef.current(volume)
    },
    [videoRef],
  )

  useEventListener('pause', onPause, videoRef.current)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const isCurrentActive =
      activeVideo?.id === id && activeVideo?.ref.current === videoRef.current

    const canPlay = isCurrentActive && canStartPlay

    if (canPlay) {
      play(video)
    } else {
      pauseVideo()
      video.currentTime = 0
    }
  }, [
    id,
    isOnScreen,
    canStartPlay,
    videoRef,
    activeVideo?.id,
    activeVideo?.ref,
  ])

  return {
    muted,
    volume,
    videoRef,
    isPlaying,
    togglePlay,
    toggleMute,
    pauseVideo,
    setIsPlaying,
    handleVolumeChange,
  }
}

export default useVideoControl
